/**
 * UranIA PA — Client-side UI audit client (US-011).
 *
 * Emette eventi audit "UI-side" (es. click dismiss banner AI Act art. 50)
 * verso il backend di compliance. Il backend WORM-grade è in scope per
 * US-F1-11 "Compliance & Audit MVP" (tabella `audit_events` Postgres
 * hash-chained) e US-F0/F1 successive; questo client è il transport
 * fire-and-forget lato browser.
 *
 * Boundary di sicurezza (analisi §19 + §20):
 *   - Niente PII oltre `subject` (sub OIDC) + `tenantId`: l'evento è
 *     `{action, actor, ts, attrs}` con `attrs` limitati a metadati UI
 *     (path, locale, viewport optional). Niente token, niente body
 *     applicativo.
 *   - Transport non-blocking: `navigator.sendBeacon` quando disponibile
 *     (RFC keepalive), altrimenti `fetch(..., {keepalive: true})`. Errori
 *     non vengono propagati: l'audit lato server (AI Gateway US-F1-11) è
 *     la source of truth — il canale UI è "best effort".
 *   - Endpoint pilotabile via `VITE_AUDIT_UI_ENDPOINT`: assente ⇒ no-op
 *     in produzione, console.info in dev (analisi §20 osservabilità).
 *
 * NB: l'endpoint `/api/v1/audit/ui-events` lato API Gateway sarà esposto
 * dalla US Compliance & Audit (US-F1-11+). Fino ad allora questo client
 * tiene il contratto stabile e non rompe il flow utente in mancanza del
 * backend.
 */

import { loadSpaEnv } from '../env.ts';

export interface UiAuditEventActor {
  readonly subject: string | null;
  readonly tenantId: string | null;
  readonly roles: readonly string[];
}

export interface UiAuditEvent {
  readonly action: string;
  readonly actor: UiAuditEventActor;
  readonly ts: string;
  readonly attrs: Readonly<Record<string, string | number | boolean | null>>;
}

const DEV_MODE = (import.meta.env.DEV as boolean | undefined) ?? false;

function postBeacon(endpoint: string, payload: string): boolean {
  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    try {
      const blob = new Blob([payload], { type: 'application/json' });
      return navigator.sendBeacon(endpoint, blob);
    } catch {
      return false;
    }
  }
  return false;
}

function postFetchKeepalive(endpoint: string, payload: string): void {
  if (typeof fetch !== 'function') {
    return;
  }
  void fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true,
    credentials: 'include',
  }).catch(() => {
    // fire-and-forget: errore lato server-side audit non interrompe la UI.
  });
}

export async function recordUiAuditEvent(event: UiAuditEvent): Promise<void> {
  const payload = JSON.stringify(event);
  const env = loadSpaEnv();
  if (DEV_MODE) {
    console.info('[urania-pa.ui-audit]', event);
  }
  if (env.auditUiEndpoint.length === 0) {
    return;
  }
  const delivered = postBeacon(env.auditUiEndpoint, payload);
  if (!delivered) {
    postFetchKeepalive(env.auditUiEndpoint, payload);
  }
  return Promise.resolve();
}
